import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
    ArrowLeft,
    RefreshCw,
    TrendingUp,
    MousePointerClick,
    Copy,
    ExternalLink,
    Globe2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EXAM_LABELS = {
    academic: "LANGUAGECERT Academic",
    esol: "LANGUAGECERT ESOL",
    lte: "LANGUAGECERT Test of English (LTE)",
    "pl-lte": "LANGUAGECERT LTE (Poland)",
    "pl-esol": "LANGUAGECERT ESOL (Poland)",
    "pl-academic": "LANGUAGECERT Academic (Poland)",
    "pl-general": "LANGUAGECERT General (Poland)",
};

const EXAM_SHORT_LABELS = {
    academic: "Academic",
    esol: "ESOL",
    lte: "LTE",
    "pl-lte": "LTE (PL)",
    "pl-esol": "ESOL (PL)",
    "pl-academic": "Academic (PL)",
    "pl-general": "General (PL)",
};

const formatExam = (id) => EXAM_LABELS[id] || id || "Unknown";
const formatExamShort = (id) => EXAM_SHORT_LABELS[id] || id || "—";

const StatCard = ({ label, value, icon: Icon, hint, testId }) => (
    <div
        data-testid={testId}
        className="bg-white border border-[#E2E8F0] rounded-xl p-6 sm:p-7 flex flex-col gap-3 transition-all hover:border-[#1B2A47] hover:shadow-[0_12px_40px_-18px_rgba(27,42,71,0.2)]"
    >
        <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#64748B]">
                {label}
            </span>
            {Icon && (
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#F1F5F9] text-[#1B2A47]">
                    <Icon className="w-4 h-4" />
                </span>
            )}
        </div>
        <div
            data-testid={`${testId}-value`}
            className="text-3xl sm:text-4xl font-semibold text-[#0A1128] font-[Outfit] tracking-tight"
        >
            {value}
        </div>
        {hint && <div className="text-xs text-[#475569]">{hint}</div>}
    </div>
);

const Bar = ({ label, value, total, color = "#1B2A47", testId }) => {
    const pct = total > 0 ? Math.round((value / total) * 100) : 0;
    return (
        <div data-testid={testId} className="space-y-1.5">
            <div className="flex items-center justify-between text-sm">
                <span className="text-[#0A1128] font-medium truncate pr-4">
                    {label}
                </span>
                <span className="text-[#475569] tabular-nums">
                    {value}
                    <span className="text-[#94A3B8] ml-2 text-xs">
                        {pct}%
                    </span>
                </span>
            </div>
            <div className="h-2 w-full bg-[#F1F5F9] rounded-full overflow-hidden">
                <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                        width: `${pct}%`,
                        backgroundColor: color,
                    }}
                />
            </div>
        </div>
    );
};

export default function AdminStatsPage() {
    const [stats, setStats] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshedAt, setRefreshedAt] = useState(null);

    const load = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await axios.get(`${API}/events/stats`);
            setStats(res.data);
            setRefreshedAt(new Date());
        } catch (e) {
            setError(e?.message || "Failed to load stats");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const derived = useMemo(() => {
        if (!stats) return null;

        const examSelects = stats.exam_selects || {};
        const codeCopies = stats.code_copies || {};
        const bookingClicks = stats.booking_clicks || {};

        // Combined exam engagement = selects + code copies + booking clicks
        const engagement = {};
        Object.entries(examSelects).forEach(([k, v]) => {
            engagement[k] = (engagement[k] || 0) + v;
        });
        const accumulateBySurface = (obj) => {
            Object.entries(obj).forEach(([key, v]) => {
                const [, examId] = key.split(":");
                if (!examId || examId === "unknown") return;
                engagement[examId] = (engagement[examId] || 0) + v;
            });
        };
        accumulateBySurface(codeCopies);
        accumulateBySurface(bookingClicks);

        const engagementEntries = Object.entries(engagement).sort(
            (a, b) => b[1] - a[1],
        );
        const topExam = engagementEntries[0] || null;

        // Main vs Poland split from code_copies + booking_clicks
        let mainCount = 0;
        let polandCount = 0;
        const splitFrom = (obj) => {
            Object.entries(obj).forEach(([key, v]) => {
                const [surface] = key.split(":");
                if (surface === "main") mainCount += v;
                else if (surface === "poland") polandCount += v;
            });
        };
        splitFrom(codeCopies);
        splitFrom(bookingClicks);
        const surfaceTotal = mainCount + polandCount;

        return {
            engagementEntries,
            topExam,
            mainCount,
            polandCount,
            surfaceTotal,
            codeCopyTotal: Object.values(codeCopies).reduce((a, b) => a + b, 0),
            bookingClickTotal: Object.values(bookingClicks).reduce(
                (a, b) => a + b,
                0,
            ),
            examSelectTotal: Object.values(examSelects).reduce(
                (a, b) => a + b,
                0,
            ),
        };
    }, [stats]);

    return (
        <div
            data-testid="admin-stats-page"
            className="min-h-screen bg-[#F8F9FA]"
        >
            {/* top bar */}
            <div className="bg-white border-b border-[#E2E8F0]">
                <div className="max-w-6xl mx-auto px-6 sm:px-8 h-16 flex items-center justify-between">
                    <Link
                        to="/"
                        data-testid="back-to-site-link"
                        className="inline-flex items-center gap-2 text-sm text-[#475569] hover:text-[#1B2A47] transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back to site
                    </Link>
                    <button
                        type="button"
                        onClick={load}
                        data-testid="refresh-stats-button"
                        className="inline-flex items-center gap-2 text-sm bg-[#1B2A47] hover:bg-[#0F172A] text-white px-4 py-2 rounded-md font-medium transition-colors disabled:opacity-60"
                        disabled={loading}
                    >
                        <RefreshCw
                            className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
                        />
                        {loading ? "Refreshing…" : "Refresh"}
                    </button>
                </div>
            </div>

            <div className="max-w-6xl mx-auto px-6 sm:px-8 py-10 sm:py-14">
                <header className="mb-10">
                    <span className="text-xs font-semibold uppercase tracking-[0.22em] text-[#D32F2F]">
                        Admin · Stats
                    </span>
                    <h1
                        data-testid="admin-stats-title"
                        className="mt-2 text-3xl sm:text-4xl font-semibold text-[#0A1128] font-[Outfit] tracking-tight"
                    >
                        LanguageCert codes dashboard
                    </h1>
                    <p className="mt-2 text-sm text-[#475569]">
                        Live counts from{" "}
                        <code className="font-mono-code text-[12px] bg-white border border-[#E2E8F0] px-1.5 py-0.5 rounded">
                            GET /api/events/stats
                        </code>
                        {refreshedAt && (
                            <span className="ml-3 text-[#94A3B8]">
                                · updated{" "}
                                {refreshedAt.toLocaleTimeString([], {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    second: "2-digit",
                                })}
                            </span>
                        )}
                    </p>
                </header>

                {error && (
                    <div
                        data-testid="stats-error"
                        className="mb-8 bg-[#FEF2F2] border border-[#FECACA] text-[#991B1B] rounded-lg px-4 py-3 text-sm"
                    >
                        Couldn&apos;t load stats: {error}
                    </div>
                )}

                {loading && !stats && (
                    <div
                        data-testid="stats-loading"
                        className="text-sm text-[#64748B] animate-pulse"
                    >
                        Loading stats…
                    </div>
                )}

                {stats && derived && (
                    <div className="space-y-10">
                        {/* KPI cards */}
                        <section
                            data-testid="kpi-grid"
                            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5"
                        >
                            <StatCard
                                testId="stat-total-events"
                                label="Total events"
                                value={stats.total_events.toLocaleString()}
                                icon={TrendingUp}
                                hint={
                                    stats.last_event_at
                                        ? `Last: ${new Date(
                                              stats.last_event_at,
                                          ).toLocaleString()}`
                                        : "No events yet"
                                }
                            />
                            <StatCard
                                testId="stat-most-clicked-exam"
                                label="Most-clicked exam"
                                value={
                                    derived.topExam
                                        ? formatExamShort(derived.topExam[0])
                                        : "—"
                                }
                                icon={MousePointerClick}
                                hint={
                                    derived.topExam
                                        ? `${formatExam(derived.topExam[0])} · ${derived.topExam[1]} interactions`
                                        : "Needs activity"
                                }
                            />
                            <StatCard
                                testId="stat-code-copies"
                                label="Code copies"
                                value={derived.codeCopyTotal.toLocaleString()}
                                icon={Copy}
                                hint={`${derived.examSelectTotal} exam selects`}
                            />
                            <StatCard
                                testId="stat-booking-clicks"
                                label="Booking clicks"
                                value={derived.bookingClickTotal.toLocaleString()}
                                icon={ExternalLink}
                                hint={`${stats.poland_scrolls} Poland scrolls`}
                            />
                        </section>

                        {/* Main vs Poland */}
                        <section
                            data-testid="surface-split-section"
                            className="bg-white border border-[#E2E8F0] rounded-xl p-6 sm:p-8"
                        >
                            <div className="flex items-center gap-3 mb-5">
                                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#F1F5F9] text-[#1B2A47]">
                                    <Globe2 className="w-4 h-4" />
                                </span>
                                <h2 className="text-lg font-semibold text-[#0A1128] font-[Outfit]">
                                    Main vs Poland split
                                </h2>
                                <span className="ml-auto text-xs text-[#64748B]">
                                    Copies + booking clicks
                                </span>
                            </div>

                            {derived.surfaceTotal === 0 ? (
                                <p
                                    data-testid="surface-split-empty"
                                    className="text-sm text-[#64748B]"
                                >
                                    No copies or booking clicks yet.
                                </p>
                            ) : (
                                <div className="space-y-4">
                                    <Bar
                                        testId="surface-bar-main"
                                        label="Main (international)"
                                        value={derived.mainCount}
                                        total={derived.surfaceTotal}
                                        color="#1B2A47"
                                    />
                                    <Bar
                                        testId="surface-bar-poland"
                                        label="Poland"
                                        value={derived.polandCount}
                                        total={derived.surfaceTotal}
                                        color="#D32F2F"
                                    />
                                </div>
                            )}
                        </section>

                        {/* Per-exam engagement */}
                        <section
                            data-testid="exam-engagement-section"
                            className="bg-white border border-[#E2E8F0] rounded-xl p-6 sm:p-8"
                        >
                            <div className="flex items-center gap-3 mb-5">
                                <h2 className="text-lg font-semibold text-[#0A1128] font-[Outfit]">
                                    Engagement by exam
                                </h2>
                                <span className="ml-auto text-xs text-[#64748B]">
                                    Selects + copies + bookings
                                </span>
                            </div>
                            {derived.engagementEntries.length === 0 ? (
                                <p
                                    data-testid="exam-engagement-empty"
                                    className="text-sm text-[#64748B]"
                                >
                                    No exam activity yet.
                                </p>
                            ) : (
                                <div className="space-y-3.5">
                                    {(() => {
                                        const topValue =
                                            derived.engagementEntries[0][1];
                                        return derived.engagementEntries.map(
                                            ([examId, v]) => (
                                                <Bar
                                                    key={examId}
                                                    testId={`exam-bar-${examId}`}
                                                    label={formatExam(examId)}
                                                    value={v}
                                                    total={topValue}
                                                    color={
                                                        examId.startsWith(
                                                            "pl-",
                                                        )
                                                            ? "#D32F2F"
                                                            : "#1B2A47"
                                                    }
                                                />
                                            ),
                                        );
                                    })()}
                                </div>
                            )}
                        </section>

                        {/* Raw breakdown */}
                        <section
                            data-testid="raw-breakdown-section"
                            className="grid grid-cols-1 md:grid-cols-3 gap-5"
                        >
                            <RawBlock
                                testId="raw-types"
                                title="Events by type"
                                entries={Object.entries(
                                    stats.events_by_type || {},
                                )}
                            />
                            <RawBlock
                                testId="raw-copies"
                                title="Code copies"
                                entries={Object.entries(
                                    stats.code_copies || {},
                                )}
                            />
                            <RawBlock
                                testId="raw-bookings"
                                title="Booking clicks"
                                entries={Object.entries(
                                    stats.booking_clicks || {},
                                )}
                            />
                        </section>
                    </div>
                )}
            </div>
        </div>
    );
}

const RawBlock = ({ title, entries, testId }) => (
    <div
        data-testid={testId}
        className="bg-white border border-[#E2E8F0] rounded-xl p-5"
    >
        <h3 className="text-sm font-semibold text-[#0A1128] mb-3">{title}</h3>
        {entries.length === 0 ? (
            <p className="text-xs text-[#94A3B8]">No data.</p>
        ) : (
            <ul className="space-y-1.5 text-sm">
                {entries
                    .sort((a, b) => b[1] - a[1])
                    .map(([key, v]) => (
                        <li
                            key={key}
                            className="flex items-center justify-between gap-3 text-[#475569]"
                        >
                            <code className="font-mono-code text-[12px] truncate text-[#0A1128]">
                                {key}
                            </code>
                            <span className="tabular-nums font-medium text-[#0A1128]">
                                {v}
                            </span>
                        </li>
                    ))}
            </ul>
        )}
    </div>
);
